import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DollarSign, CheckCircle, AlertCircle } from "lucide-react";

const PricingSection = () => {
  const pricingPoints = [
    "Landscaping costs vary depending on property size and materials",
    "Written estimate provided before project begins",
    "No hidden fees or surprise charges",
    "Flexible payment options available",
    "Free initial consultation and property assessment",
  ];

  return (
    <section
      data-testid="pricing-section"
      className="section-padding bg-[#2D5A27]"
    >
      <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div>
            <span className="inline-flex items-center gap-2 text-white/80 font-medium text-sm mb-4">
              <DollarSign className="w-4 h-4" />
              Transparent Pricing
            </span>
            <h2
              data-testid="pricing-headline"
              className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-6"
            >
              Honest Pricing, No Surprises
            </h2>
            <p className="text-white/80 text-base md:text-lg mb-8 leading-relaxed">
              We believe in complete transparency. Every project is unique, and our pricing reflects the specific needs of your property and desired services.
            </p>

            {/* Pricing Points */}
            <ul className="space-y-4 mb-10">
              {pricingPoints.map((point, index) => (
                <li
                  key={index}
                  data-testid={`pricing-point-${index}`}
                  className="flex items-start gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-[#C2410C] flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">{point}</span>
                </li>
              ))}
            </ul>

            <Link to="/contact">
              <Button
                data-testid="pricing-cta"
                className="bg-[#C2410C] hover:bg-[#9A3412] text-white h-14 px-10 text-lg rounded-full cta-accent"
              >
                Get Your Free Estimate
              </Button>
            </Link>
          </div>

          {/* Right Card */}
          <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#F3F4F1] rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-[#2D5A27]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1A2C22]">
                Important Information
              </h3>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-[#1A2C22] mb-2">
                  Property Assessment
                </h4>
                <p className="text-[#4B5563] text-sm leading-relaxed">
                  We visit your property to assess the scope of work, soil conditions, and specific requirements before providing an estimate.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-[#1A2C22] mb-2">
                  What Affects Pricing
                </h4>
                <ul className="text-[#4B5563] text-sm space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#2D5A27] rounded-full" />
                    Property size and terrain
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#2D5A27] rounded-full" />
                    Materials and plants selected
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#2D5A27] rounded-full" />
                    Complexity of design
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#2D5A27] rounded-full" />
                    Timeline requirements
                  </li>
                </ul>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <p className="text-[#6B7280] text-xs leading-relaxed">
                  All estimates are provided in writing and remain valid for 30 days. We require a deposit to begin work, with the balance due upon completion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
